import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import Editor, { type OnMount } from '@monaco-editor/react';
import { cn } from 'src/lib/utils';
import type { DataTransferSqlMetadata } from '../types/data-transfers.types';

interface SqlEditorProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  metadata?: DataTransferSqlMetadata | null;
  readOnly?: boolean;
  className?: string;
  height?: number | string;
}

export interface SqlEditorHandle {
  insertAtCursor: (text: string) => void;
  focus: () => void;
}

type Monaco = Parameters<OnMount>[1];
type MonacoEditor = Parameters<OnMount>[0];

type EditorRange = {
  startLineNumber: number;
  endLineNumber: number;
  startColumn: number;
  endColumn: number;
};

type CompletionItem = {
  label: string;
  kind: number;
  insertText: string;
  detail?: string;
  filterText?: string;
  sortText?: string;
  range: EditorRange;
};

type PathContext =
  | { stage: 'root'; prefix: string; range: EditorRange }
  | { stage: 'dataset'; project: string; prefix: string; range: EditorRange }
  | { stage: 'table'; project: string; dataset: string; prefix: string; range: EditorRange }
  | { stage: 'column'; tableName: string; prefix: string; range: EditorRange }
  | { stage: 'general'; prefix: string; range: EditorRange };

function matchesPrefix(value: string, prefix: string): boolean {
  if (!prefix) {
    return true;
  }
  return value.toLowerCase().startsWith(prefix.toLowerCase());
}

function getPathContext(
  linePrefix: string,
  lineNumber: number,
  cursorColumn: number
): PathContext {
  const tokenMatch = /[`A-Za-z0-9_.-]+$/.exec(linePrefix);
  if (!tokenMatch) {
    return {
      stage: 'general',
      prefix: '',
      range: {
        startLineNumber: lineNumber,
        endLineNumber: lineNumber,
        startColumn: cursorColumn,
        endColumn: cursorColumn,
      },
    };
  }

  const token = tokenMatch[0];
  const tokenStartIndex = tokenMatch.index;
  const tokenStartColumn = tokenStartIndex + 1;
  const body = token.startsWith('`') ? token.slice(1) : token;
  const parts = body.split('.');
  const prefix = parts[parts.length - 1] ?? '';
  const completed = parts.slice(0, -1);

  // Replace only the current path segment (text after the last dot).
  const lastDotInToken = token.lastIndexOf('.');
  const segmentStartColumn = lastDotInToken >= 0
    ? tokenStartColumn + lastDotInToken + 1
    : tokenStartColumn;
  const range: EditorRange = {
    startLineNumber: lineNumber,
    endLineNumber: lineNumber,
    startColumn: segmentStartColumn,
    endColumn: cursorColumn,
  };

  if (completed.length === 0) {
    return { stage: 'root', prefix, range };
  }
  if (completed.length === 1) {
    return { stage: 'dataset', project: completed[0], prefix, range };
  }
  if (completed.length === 2) {
    return {
      stage: 'table',
      project: completed[0],
      dataset: completed[1],
      prefix,
      range,
    };
  }

  return {
    stage: 'column',
    tableName: completed[2],
    prefix,
    range,
  };
}

function buildCompletionItems(
  monaco: Monaco,
  metadata: DataTransferSqlMetadata | null | undefined,
  context: PathContext
): CompletionItem[] {
  if (!metadata) {
    return [];
  }

  const { range, prefix } = context;
  const items: CompletionItem[] = [];

  const push = (item: Omit<CompletionItem, 'range'>) => {
    if (!matchesPrefix(item.filterText ?? item.label, prefix)) {
      return;
    }
    items.push({ ...item, range });
  };

  if (context.stage === 'root' || context.stage === 'general') {
    push({
      label: metadata.project_id,
      kind: monaco.languages.CompletionItemKind.Module,
      insertText: metadata.project_id,
      detail: 'BigQuery project',
      sortText: '0_project',
    });
    // Allow typing a bare table name and expanding to the full FQN.
    Object.entries(metadata.table_fqns).forEach(([tableName, fqn]) => {
      push({
        label: tableName,
        kind: monaco.languages.CompletionItemKind.Class,
        insertText: `\`${fqn}\``,
        detail: fqn,
        filterText: tableName,
        sortText: `2_${tableName}`,
      });
    });
  }

  if (context.stage === 'dataset') {
    // After project. — only suggest datasets, never re-suggest the project.
    push({
      label: metadata.mart_dataset,
      kind: monaco.languages.CompletionItemKind.Module,
      insertText: metadata.mart_dataset,
      detail: `${context.project}.${metadata.mart_dataset}`,
      sortText: '0_dataset',
    });
  }

  if (context.stage === 'table') {
    // After project.dataset. — only suggest table names for this segment.
    Object.keys(metadata.tables).forEach((tableName) => {
      push({
        label: tableName,
        kind: monaco.languages.CompletionItemKind.Class,
        insertText: tableName,
        detail: `${context.project}.${context.dataset}.${tableName}`,
        sortText: `0_${tableName}`,
      });
    });
  }

  if (context.stage === 'column') {
    const columns = metadata.tables[context.tableName] || [];
    columns.forEach((column) => {
      push({
        label: column,
        kind: monaco.languages.CompletionItemKind.Field,
        insertText: column,
        detail: `${context.tableName}.${column}`,
        sortText: `0_${column}`,
      });
    });
  }

  return items;
}

export const SqlEditor = forwardRef<SqlEditorHandle, SqlEditorProps>(function SqlEditor({
  id,
  value,
  onChange,
  metadata = null,
  readOnly = false,
  className,
  height = 360,
}, ref) {
  const monacoRef = useRef<Monaco | null>(null);
  const editorRef = useRef<MonacoEditor | null>(null);
  const providerRef = useRef<{ dispose: () => void } | null>(null);
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  useImperativeHandle(ref, () => ({
    insertAtCursor: (text: string) => {
      const editor = editorRef.current;
      if (!editor || readOnly) {
        return;
      }
      const selection = editor.getSelection();
      if (!selection) {
        return;
      }
      editor.executeEdits('insert-reference-id', [
        {
          range: selection,
          text,
          forceMoveMarkers: true,
        },
      ]);
      editor.setPosition({
        lineNumber: selection.startLineNumber,
        column: selection.startColumn + text.length,
      });
      editor.focus();
    },
    focus: () => {
      editorRef.current?.focus();
    },
  }), [readOnly]);

  const registerProvider = (monaco: Monaco) => {
    providerRef.current?.dispose();
    providerRef.current = monaco.languages.registerCompletionItemProvider('sql', {
      triggerCharacters: ['.', '`', "'", '"', ' '],
      provideCompletionItems: (model, position) => {
        const linePrefix = model
          .getLineContent(position.lineNumber)
          .slice(0, position.column - 1);
        const context = getPathContext(linePrefix, position.lineNumber, position.column);
        const suggestions = buildCompletionItems(monaco, metadataRef.current, context);
        return { suggestions };
      },
    });
  };

  useEffect(() => {
    if (monacoRef.current) {
      registerProvider(monacoRef.current);
    }
    return () => {
      providerRef.current?.dispose();
      providerRef.current = null;
    };
  }, [metadata]);

  const handleMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    registerProvider(monaco);

    // Cmd+Space is Spotlight on macOS. Use Control+Space and Option+/ instead.
    editor.addCommand(monaco.KeyMod.WinCtrl | monaco.KeyCode.Space, () => {
      void editor.trigger('keyboard', 'editor.action.triggerSuggest', {});
    });
    editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.Slash, () => {
      void editor.trigger('keyboard', 'editor.action.triggerSuggest', {});
    });
  };

  const options = useMemo(() => ({
    readOnly,
    minimap: { enabled: false },
    fontSize: 13,
    lineHeight: 20,
    tabSize: 2,
    wordWrap: 'on' as const,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    padding: { top: 12, bottom: 12 },
    suggestOnTriggerCharacters: true,
    quickSuggestions: true,
  }), [readOnly]);

  return (
    <div className={cn('space-y-1', className)}>
      <div
        id={id}
        className="overflow-hidden rounded border border-gray-400 bg-[#f8fafc]"
      >
        <Editor
          height={height}
          defaultLanguage="sql"
          language="sql"
          theme="vs"
          value={value}
          onChange={(next) => onChange(next ?? '')}
          onMount={handleMount}
          options={options}
          loading={(
            <div className="p-3 text-sm text-gray-500">
              Loading SQL editor…
            </div>
          )}
        />
      </div>
      {!readOnly ? (
        <p className="text-xs text-gray-500">
          {'Suggestions: Control+Space or Option+/. Type project.dataset.table — each segment suggests the next.'}
        </p>
      ) : null}
    </div>
  );
});
